import { TransactionRequest } from '@ethersproject/providers'
import { PERIOD_IN_SECONDS, Period } from '@kwenta/sdk/constants'
import {
	FuturesPositionHistory,
	FuturesMarketAsset,
	NetworkId,
	TransactionStatus,
	FuturesMarginType,
} from '@kwenta/sdk/types'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { BigNumber, Signer } from 'ethers'
import { getAddress } from 'ethers/lib/utils.js'

import { notifyError } from 'components/ErrorNotifier'
import { monitorAndAwaitTransaction } from 'state/app/helpers'
import {
	handleTransactionError,
	setOpenModal,
	setTransaction,
	updateTransactionHash,
	updateTransactionStatus,
} from 'state/app/reducer'
import { ZERO_CM_FEES, ZERO_STATE_TRADE_INPUTS } from 'state/constants'
import {
	editCrossMarginTradeSize,
	fetchV3Markets,
	fetchPositionHistoryV3,
} from 'state/futures/crossMargin/actions'
import { AppThunk } from 'state/store'
import { ThunkConfig } from 'state/types'
import { setAbstractionAddress } from 'state/wallet/reducer'
import {
	selectAbstractionAddress,
	selectProviderNetwork,
	selectWallet,
} from 'state/wallet/selectors'
import { DateInterval, INTERVAL_OPTIONS } from 'utils/dates'
import { serializePerpsV2PositionHistory } from 'utils/futures'

import { selectFuturesType, selectTradingMode } from './common/selectors'
import { setCrossMarginMarketAsset } from './crossMargin/reducer'
import {
	addDelegateToAddressBook,
	addDelegateToDelegatesList,
	removeDelegateFromAddressBook,
	removeDelegateFromDelegatesList,
	setDelegated,
	setSession,
} from './reducer'
import {
	selectAddressBook,
	selectNewNickname,
	selectSelectedDelegationAddress,
	selectAbstractionDelegated,
	selectSessionValid,
} from './selectors'
import {
	editSmartMarginTradeSize,
	fetchDailyVolumesV2,
	fetchMarginTransfersV2,
	fetchMarketsV2,
	fetchPositionHistoryV2,
	fetchSmartMarginOpenOrders,
} from './smartMargin/actions'
import {
	setSmartMarginLeverageInput,
	setSmartMarginFees,
	setSmartMarginMarginDelta,
	clearSmartMarginTradePreviews,
	setSmartMarginTradeInputs,
	setSmartMarginEditPositionInputs,
	setSmartMarginMarketAsset,
} from './smartMargin/reducer'
import { selectSmartMarginAccount, selectSignerSupportsSmartMargin } from './smartMargin/selectors'
import { DelegationAccountInfo, TradingModes } from './types'

export const setMarketAsset = createAsyncThunk<void, FuturesMarketAsset, ThunkConfig>(
	'futures/fetchMarkets',
	async (asset, { dispatch, getState }) => {
		if (selectFuturesType(getState()) === FuturesMarginType.SMART_MARGIN) {
			dispatch(setSmartMarginMarketAsset(asset))
		} else {
			dispatch(setCrossMarginMarketAsset(asset))
		}
	}
)

export const fetchMarkets = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchMarkets',
	async (_, { dispatch, getState }) => {
		if (getState().futures.selectedType === FuturesMarginType.SMART_MARGIN) {
			dispatch(fetchMarketsV2())
		} else {
			dispatch(fetchV3Markets())
		}
	}
)

export const fetchDailyVolumes = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchDailyVolumes',
	async (_, { dispatch }) => {
		// TODO: Fetch v3 daily volumes
		dispatch(fetchDailyVolumesV2())
	}
)

export const fetchMarginTransfers = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchMarginTransfers',
	async (_, { dispatch }) => {
		// TODO: Conditionally fetch cross / smart
		dispatch(fetchMarginTransfersV2())
	}
)

export const clearTradeInputs = createAsyncThunk<void, void, ThunkConfig>(
	'futures/clearTradeInputs',
	async (_, { dispatch }) => {
		// TODO: Separate to cross / smart
		dispatch(setSmartMarginMarginDelta(''))
		dispatch(setSmartMarginFees(ZERO_CM_FEES))
		dispatch(setSmartMarginLeverageInput(''))
		dispatch(clearSmartMarginTradePreviews())
		dispatch(setSmartMarginTradeInputs(ZERO_STATE_TRADE_INPUTS))
		dispatch(setSmartMarginEditPositionInputs({ nativeSizeDelta: '', marginDelta: '' }))
	}
)

export const editTradeSizeInput =
	(size: string, currencyType: 'usd' | 'native'): AppThunk =>
	(dispatch, getState) => {
		const type = selectFuturesType(getState())
		if (type === FuturesMarginType.CROSS_MARGIN) {
			dispatch(editCrossMarginTradeSize(size, currencyType))
		} else {
			dispatch(editSmartMarginTradeSize(size, currencyType))
		}
	}

export const fetchFuturesPositionHistory = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchFuturesPositionHistory',
	async (_, { getState, dispatch }) => {
		const accountType = selectFuturesType(getState())
		accountType === FuturesMarginType.CROSS_MARGIN
			? dispatch(fetchPositionHistoryV3())
			: dispatch(fetchPositionHistoryV2())
	}
)

export const fetchPositionHistoryForTrader = createAsyncThunk<
	{ history: FuturesPositionHistory<string>[]; address: string; networkId: NetworkId } | undefined,
	string,
	ThunkConfig
>('futures/fetchPositionHistoryForTrader', async (traderAddress, { getState, extra: { sdk } }) => {
	try {
		const networkId = selectProviderNetwork(getState())
		const futuresSupported = selectSignerSupportsSmartMargin(getState())
		if (!futuresSupported) return
		const history = await sdk.futures.getPositionHistory(traderAddress, 'eoa')
		return { history: serializePerpsV2PositionHistory(history), networkId, address: traderAddress }
	} catch (err) {
		notifyError('Failed to fetch history for trader ' + traderAddress, err)
		throw err
	}
})

// Contract Mutations

export const cancelDelayedOrder = createAsyncThunk<void, string, ThunkConfig>(
	'futures/cancelDelayedOrder',
	async (marketAddress, { getState, dispatch, extra: { sdk } }) => {
		const account = selectSmartMarginAccount(getState())
		if (!account) throw new Error('No wallet connected')
		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'cancel_delayed_isolated',
					hash: null,
				})
			)

			const tx = await sdk.futures.cancelDelayedOrder({
				marketAddress,
				account,
				isOffchain: true,
			})
			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(fetchSmartMarginOpenOrders())
		} catch (err) {
			dispatch(handleTransactionError(err.message))
			throw err
		}
	}
)

// Utils

export const fetchFundingRatesHistory = createAsyncThunk<
	{ marketAsset: FuturesMarketAsset; rates: any },
	{ marketAsset: FuturesMarketAsset; period: Period },
	ThunkConfig
>('futures/fetchFundingRatesHistory', async ({ marketAsset, period }, { extra: { sdk } }) => {
	const rates = await sdk.futures.getMarketFundingRatesHistory(
		marketAsset,
		PERIOD_IN_SECONDS[period]
	)
	return { marketAsset, rates }
})

// Delegate
export const addDelegate = createAsyncThunk<void, void, ThunkConfig>(
	'futures/addDelegate',
	async (_, { getState, dispatch, extra: { sdk } }) => {
		const state = getState()
		const account = selectSmartMarginAccount(state)
		const wallet = selectWallet(state)
		const delegateAddress = selectSelectedDelegationAddress(getState())
		const newNickname = selectNewNickname(getState())

		if (!account) {
			notifyError('No smart margin account')
			return
		}

		if (!wallet) {
			notifyError('No wallet connected')
			return
		}

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'add_delegate',
					hash: null,
				})
			)
			const tx = await sdk.futures.addDelegate({ account, delegateAddress })
			const delegate = { address: delegateAddress, nickname: newNickname }
			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(addDelegateToAddressBook({ wallet, delegate }))
			dispatch(addDelegateToDelegatesList({ address: delegateAddress, nickname: newNickname }))
			dispatch(fetchDelegatesForAccount())
			dispatch(fetchSubAccountsForAccount())
		} catch (err) {
			dispatch(handleTransactionError(err.message))
			throw err
		}
	}
)

export const removeDeledgate = createAsyncThunk<void, void, ThunkConfig>(
	'futures/removeDelegate',
	async (_, { getState, dispatch, extra: { sdk } }) => {
		const state = getState()
		const wallet = selectWallet(state)
		const account = selectSmartMarginAccount(state)
		const delegateAddress = selectSelectedDelegationAddress(getState())

		if (!account) {
			notifyError('No smart margin account')
			return
		}

		if (!wallet) {
			notifyError('No wallet connected')
			return
		}

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'remove_delegate',
					hash: null,
				})
			)
			const tx = await sdk.futures.removeDelegate({ account, delegateAddress })
			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(removeDelegateFromAddressBook({ wallet, delegateAddress }))
			dispatch(removeDelegateFromDelegatesList(delegateAddress))
			dispatch(fetchDelegatesForAccount())
			dispatch(fetchSubAccountsForAccount())
		} catch (err) {
			dispatch(handleTransactionError(err.message))
			throw err
		}
	}
)

export const fetchDelegatesForAccount = createAsyncThunk<
	| {
			delegatesWithNicknames: DelegationAccountInfo[]
			account: string
			wallet: string
			networkId: NetworkId
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchDelegatesForAccount', async (_, { getState, extra: { sdk } }) => {
	const account = selectSmartMarginAccount(getState())
	const wallet = selectWallet(getState())
	const networkId = selectProviderNetwork(getState())
	const futuresSupported = selectSignerSupportsSmartMargin(getState())
	const addressBook = selectAddressBook(getState())

	if (!futuresSupported || !wallet || !account) return

	try {
		const delegates = await sdk.futures.getDelegatesForAccount()
		const addressMap = Object.fromEntries(
			addressBook.map((d) => [getAddress(d.address), d.nickname])
		)
		const delegatesWithNicknames = delegates.map(({ delegate }) => {
			const address = getAddress(delegate)
			const nickname = addressMap[address] || 'New Delegate'
			return { address, nickname }
		}) as DelegationAccountInfo[]

		return {
			wallet,
			account,
			networkId,
			delegatesWithNicknames,
		}
	} catch (err) {
		notifyError('Failed to fetch delegates', err)
		throw err
	}
})

export const fetchSubAccountsForAccount = createAsyncThunk<
	| {
			subAccountsWithNicknames: DelegationAccountInfo[]
			wallet: string
			networkId: NetworkId
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchSubAccountsForAccount', async (_, { getState, extra: { sdk } }) => {
	const wallet = selectWallet(getState())
	const networkId = selectProviderNetwork(getState())
	const futuresSupported = selectSignerSupportsSmartMargin(getState())
	const addressBook = selectAddressBook(getState())

	if (!futuresSupported || !wallet) return

	try {
		const subAccounts = await sdk.futures.getSubAccountsForAccount()
		const addressMap = Object.fromEntries(
			addressBook.map((d) => [getAddress(d.address), d.nickname])
		)
		const subAccountsWithNicknames = subAccounts.map(({ caller }) => {
			const address = getAddress(caller)
			const nickname = addressMap[address] || 'New Sub-Account'
			return { address, nickname }
		}) as DelegationAccountInfo[]
		return {
			wallet,
			networkId,
			subAccountsWithNicknames,
		}
	} catch (err) {
		notifyError('Failed to fetch sub-accounts', err)
		throw err
	}
})

// One-Click Trading
export const delegateToOneClickTrading = createAsyncThunk<void, void, ThunkConfig>(
	'futures/delegateToOneClickTrading',
	async (_, { getState, dispatch, extra: { accountAbstraction, sdk } }) => {
		const address = accountAbstraction.accountAddress
		const smAccount = selectSmartMarginAccount(getState())

		if (!smAccount || !address) throw new Error('No wallet connected')

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'delegate_to_smart_margin',
					hash: null,
				})
			)
			const tx = await sdk.futures.delegateSmartMarginAccount({
				account: smAccount,
				delegate: address,
			})

			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(setDelegated(true))
		} catch (err) {
			dispatch(handleTransactionError(err.message))
			dispatch(fetchAbstractionDelegateStatus())
		}
	}
)

export const startOneClickSession = createAsyncThunk<void, DateInterval | number, ThunkConfig>(
	'futures/delegateToOneClickTrading',
	async (interval, { getState, dispatch, extra: { accountAbstraction } }) => {
		const address = accountAbstraction.accountAddress
		const smAccount = selectSmartMarginAccount(getState())

		if (!smAccount || !address) throw new Error('No wallet connected')

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'create_session',
					hash: null,
				})
			)
			const session = await accountAbstraction.createSession(interval, smAccount)

			dispatch(setSession(session))
			dispatch(updateTransactionStatus(TransactionStatus.Confirmed))
		} catch (err) {
			dispatch(handleTransactionError(err.message))
		}
	}
)

export const closeOneClickSession = createAsyncThunk<void, void, ThunkConfig>(
	'futures/delegateToOneClickTrading',
	async (_, { getState, dispatch, extra: { accountAbstraction } }) => {
		const address = accountAbstraction.accountAddress
		const smAccount = selectSmartMarginAccount(getState())

		if (!smAccount || !address) throw new Error('No wallet connected')

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'close_session',
					hash: null,
				})
			)

			const session = await accountAbstraction.closeAllSessions()

			dispatch(setSession(session))
			dispatch(updateTransactionStatus(TransactionStatus.Confirmed))
		} catch (err) {
			dispatch(handleTransactionError(err.message))
		}
	}
)

export const depositFundsToOneClick = createAsyncThunk<void, BigNumber, ThunkConfig>(
	'futures/depositFundsToOneClick',
	async (amount, { getState, dispatch, extra: { sdk } }) => {
		const state = getState()
		const abstractionAddress = selectAbstractionAddress(state)

		const signer = sdk.context.signer

		if (!signer || !abstractionAddress) throw new Error('No account connected')

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'deposit_one_click',
					hash: null,
				})
			)

			const tx = await signer.sendTransaction({
				to: abstractionAddress,
				value: amount,
			})

			await monitorAndAwaitTransaction(dispatch, tx)
		} catch (err) {
			dispatch(handleTransactionError(err.message))
		}
	}
)

export const withdrawFundsFromOneClick = createAsyncThunk<void, BigNumber, ThunkConfig>(
	'futures/withdrawFundsFromOneClick',
	async (amount, { getState, dispatch, extra: { accountAbstraction } }) => {
		const smAccount = selectSmartMarginAccount(getState())
		const accountAddress = selectWallet(getState())

		if (!smAccount || !accountAddress) throw new Error('No account connected')

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'withdraw_one_click',
					hash: null,
				})
			)

			const tx: TransactionRequest = {
				to: accountAddress,
				value: amount,
			}

			const receipt = await accountAbstraction.sendUserOperations([tx], {})

			dispatch(updateTransactionHash(receipt.transactionHash))
			dispatch(updateTransactionStatus(TransactionStatus.Confirmed))
		} catch (err) {
			dispatch(handleTransactionError(err.message))
		}
	}
)

export const initAccountAbstraction = createAsyncThunk<
	void,
	Signer | null | undefined,
	ThunkConfig
>('futures/initAccountAbstraction', async (signer, { dispatch, extra: { accountAbstraction } }) => {
	if (signer) {
		try {
			await accountAbstraction.init(signer)
			const { accountAddress } = accountAbstraction

			if (!accountAddress) return

			const session = await accountAbstraction.getSessionInfo()

			dispatch(setSession(session))
			dispatch(setAbstractionAddress(accountAddress))
		} catch (err) {
			dispatch(handleTransactionError(err.message))
		}
	} else {
		accountAbstraction.disconnect()
	}
})

export const fetchAbstractionDelegateStatus = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchAbstractionDelegateStatus',
	async (_, { getState, dispatch, extra: { sdk } }) => {
		const abstractionAddress = selectAbstractionAddress(getState())
		const smAddress = selectSmartMarginAccount(getState())

		if (!abstractionAddress || !smAddress) throw new Error('No account connected')

		const isDelegate = await sdk.futures.checkDelegateSmartMarginAccount({
			account: smAddress,
			delegate: abstractionAddress,
		})

		dispatch(setDelegated(isDelegate))
	}
)

export const handleOneClickOperation = createAsyncThunk<void, VoidFunction, ThunkConfig>(
	'futures/handleOneClickOperation',
	async (callback, { getState, dispatch }) => {
		const tradingMode = selectTradingMode(getState())
		const isSessionActive = selectSessionValid(getState())
		const isDelegated = selectAbstractionDelegated(getState())

		const isNeedActivate = !isSessionActive && tradingMode === TradingModes.ONE_CLICK

		try {
			if (isNeedActivate) {
				isDelegated
					? await dispatch(startOneClickSession(INTERVAL_OPTIONS.at(0)!))
					: await dispatch(setOpenModal('one_click_trading_onboard'))
			}

			return callback()
		} catch (err) {
			dispatch(handleTransactionError(err.message))
		}
	}
)
